import { useSelector } from 'react-redux';
import { getUser } from 'store/selectors/auth.selector';
import { TableView, TableName } from 'modules/core/enums';
import { TableColumn } from 'modules/core/components/Table/types';
import { Column } from 'react-table';
import {
  COLOR_TABLE_COLUMN,
  COLOR_TABLE_COLUMN_RAC,
  WITHOUT_INVENTORY_LENDER_TABLE_COLUMNS,
  WITHOUT_INVENTORY_RAC_TABLE_COLUMNS,
} from 'modules/core/components/VehicleTable/vehicleTableColumns';
import {
  RAC_VEHICLE_TABLE_COLUMN_ID,
  VEHICLE_TABLE_COLUMN_ID,
  LENDER_VEHICLE_TABLE_COLUMN_ID,
} from 'modules/core/components/VehicleTable/defaultVehicleTableColumns';
import i18n from 'i18n';

import { GET_DEFAULT_COLUMNS_MAP, COLUMNS_MAP_BY_TABLE } from '../common.const';

const withEstimatedExtraCashHeader = (columns: TableColumn[]): TableColumn[] =>
  columns.map((col) => {
    const isExtraCash =
      col.id === RAC_VEHICLE_TABLE_COLUMN_ID.EXTRA_CASH || col.id === LENDER_VEHICLE_TABLE_COLUMN_ID.SALESPERSON_BUCKET;

    return isExtraCash ? { ...col, Header: i18n.t('components.table.columns.estimatedExtraCash') as string } : col;
  });

// Color column is only surfaced for wholesale-inventory users. Insert it right
// after the `description` / Vehicle column so it reads naturally in the table.
// RAC/Lender tables wrap every cell in OpenLoanVariablesDialog, so use the
// dedicated RAC variant that includes that wrapper to maintain consistent padding.
const withColorColumn = (columns: TableColumn[], isRacOrLender = false): TableColumn[] => {
  if (columns.some((col) => col.id === VEHICLE_TABLE_COLUMN_ID.COLOR)) return columns;

  const anchorIds = [VEHICLE_TABLE_COLUMN_ID.VEHICLE, RAC_VEHICLE_TABLE_COLUMN_ID.VEHICLE];
  const anchorIndex = columns.findIndex((col) => anchorIds.includes(col.id));
  const insertAt = anchorIndex >= 0 ? anchorIndex + 1 : columns.length;

  const colorCol = (isRacOrLender ? COLOR_TABLE_COLUMN_RAC : COLOR_TABLE_COLUMN) as TableColumn;

  return [...columns.slice(0, insertAt), colorCol, ...columns.slice(insertAt)];
};

export const useColumnsByRole = (selectedTableView: TableView, tableName: TableName): TableColumn[] => {
  const user = useSelector(getUser);
  const DEFAULT_COLUMNS_MAP = GET_DEFAULT_COLUMNS_MAP.get(tableName) as Column<Record<string, unknown>>[];
  const isRac = tableName === TableName.RAC;
  const isLender = tableName === TableName.LENDER;

  if (!user) {
    return DEFAULT_COLUMNS_MAP;
  }

  if (!user.isWithInventory && isRac) {
    if (user.withWholesaleInventory) {
      const columns = (
        COLUMNS_MAP_BY_TABLE.get(tableName)?.get(user.role)?.get(selectedTableView) || DEFAULT_COLUMNS_MAP
      )
        .filter(
          (column) =>
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.AGE &&
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.LOCATION &&
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.BOOK_VALUE &&
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.VALUATION_GUIDE &&
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.UNIT_COST &&
            column.id !== RAC_VEHICLE_TABLE_COLUMN_ID.PROFIT &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.THIRD_ROW_SEAT,
        )
        .map((column) => (column.id === RAC_VEHICLE_TABLE_COLUMN_ID.TERM ? { ...column, isVisible: true } : column));

      return withColorColumn(withEstimatedExtraCashHeader(columns), true);
    }

    return WITHOUT_INVENTORY_RAC_TABLE_COLUMNS;
  }

  if (!user.isWithInventory && isLender) {
    if (user.withWholesaleInventory) {
      const columns = (
        COLUMNS_MAP_BY_TABLE.get(tableName)?.get(user.role)?.get(selectedTableView) || DEFAULT_COLUMNS_MAP
      )
        .filter(
          (column) =>
            column.id !== VEHICLE_TABLE_COLUMN_ID.AGE &&
            column.id !== LENDER_VEHICLE_TABLE_COLUMN_ID.LOCATION &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.BOOK_VALUE &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.VALUATION_GUIDE &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.UNIT_COST &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.DEALER_PROFIT &&
            column.id !== LENDER_VEHICLE_TABLE_COLUMN_ID.ESTIMATED_PROFIT &&
            column.id !== VEHICLE_TABLE_COLUMN_ID.THIRD_ROW_SEAT,
        )
        .map((column) =>
          column.id === LENDER_VEHICLE_TABLE_COLUMN_ID.RECOMMENDED_TERM ? { ...column, isVisible: true } : column,
        );

      return withColorColumn(withEstimatedExtraCashHeader(columns), true);
    }

    return WITHOUT_INVENTORY_LENDER_TABLE_COLUMNS;
  }

  const baseColumns =
    COLUMNS_MAP_BY_TABLE.get(tableName)?.get(user.role)?.get(selectedTableView) || DEFAULT_COLUMNS_MAP;

  if (user.withWholesaleInventory) {
    return withColorColumn(
      baseColumns.filter((col) => col.id !== VEHICLE_TABLE_COLUMN_ID.THIRD_ROW_SEAT),
      isRac || isLender,
    );
  }

  return baseColumns;
};
